import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { forexApi, type MasterAccount, type ProvisioningJob } from "@/lib/forex-api";
import OnboardingDiagnostic from './OnboardingDiagnostic';

const masterJobLabels: Record<string, string> = {
  queued: "等待可用终端", preparing: "准备终端", logging_in: "登录账号", verifying: "验证 EA 连接",
  connected: "连接验证已完成", failed: "开通失败", canceled: "已取消", canceling: "正在取消",
};

export default function MasterProvisioningStatus({ account }: { account: MasterAccount }) {
  const [job, setJob] = useState<ProvisioningJob | null>(null);
  const [error, setError] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    let canceled = false;
    let pending = false;
    setLoaded(false);
    setJob(null);
    const refresh = async () => {
      if (pending) return;
      pending = true;
      try {
        const result = await forexApi.listProvisioningJobs({target_type: "master_account", target_id: account.master_account_id});
        if (!canceled) {
          setJob([...result.items].sort((a, b) => b.created_at - a.created_at)[0] || null);
          setError(""); setLoaded(true);
        }
      } catch { if (!canceled) setError("开通进度暂不可确认，请稍后重试"); }
      finally { pending = false; }
    };
    void refresh();
    const timer = setInterval(() => void refresh(), 5000);
    return () => { canceled = true; clearInterval(timer); };
  }, [account.master_account_id]);
  const retry = async () => {
    if (!job?.can_retry || (job.diagnostics && !job.diagnostics.retryable) || !job.revision) return;
    setBusy(true);
    try { setJob(await forexApi.managedJobAction(job.provision_job_id, "retry", job.revision)); setError(""); }
    catch { setError("提交结果待核对，请检查原开通任务；再次提交会复用原请求"); }
    finally { setBusy(false); }
  };
  return <div className="rounded-lg border border-surface-border bg-white p-4 space-y-2">
    <p className="text-sm font-medium">EA 自动开通</p>
    {error && <p role="alert" className="text-sm text-red-500">{error}</p>}
    {!loaded && !error && <p className="text-xs">正在检查开通状态…</p>}
    {job && <>
      <p className="text-sm">{masterJobLabels[job.automation_stage || ""] || job.status} · {job.provision_job_id}</p>
      {job.status === "approved" && <p className="text-xs">{account.terminal_count === 0 ? "EA 已连接，后台资料同步中" : account.connection_status === "connected" ? "账号已连接" : "任务已完成，当前连接待确认"}</p>}
      {(job.last_error_code || job.last_error) && <p className="text-xs text-red-500">{job.last_error_code || job.last_error}</p>}
      <OnboardingDiagnostic diagnostics={job.diagnostics}/>
      <Link className="text-xs text-brand underline" to={`/forex/terminal/detail?job_id=${encodeURIComponent(job.provision_job_id)}`}>查看开通诊断与记录</Link>
      {job.can_retry && (!job.diagnostics || job.diagnostics.retryable) && <button disabled={busy || !!error} onClick={() => void retry()} className="text-xs text-brand">重试开通</button>}
    </>}
    {loaded && !error && !job && account.terminal_count === 0 && <Link className="text-sm text-brand" to={`/forex/master-accounts/bind?master_account_id=${encodeURIComponent(account.master_account_id)}`}>{account.credential_binding_id ? "开通 EA" : "补充凭证并开通 EA"}</Link>}
    {account.terminal_count > 0 && !job && <p className="text-xs">已有 Terminal，请查看连接状态；自动开通不会接管现有终端。</p>}
    <p className="text-xs text-text-muted">开通仅建立 EA 连接，策略与交易授权需分别配置。</p>
  </div>;
}
